/**
 * The country's SMSWiser campaign groups on the client — the SAME shared
 * formula and order as the server (wiserId ascending), so labels match the
 * board's: which group a campaign is in, who is in a group, its label.
 * For Media Devoted | Created By Ciro Autiero
 */
import { type CampaignGroup, groupCampaigns, type NamedGroupableCampaign, type TrackerEnum } from '@trafficpulse/shared';

export interface CatalogCampaignLike {
  id: number;
  name: string;
  trafficProvider?: TrackerEnum | null;
  trafficCampaignId?: string | null;
  voluumCampaignId?: string | null;
}

export interface GroupIndex {
  groups: CampaignGroup<NamedGroupableCampaign>[];
  keyOf: (wiserId: number) => string;
  group: (key: string) => CampaignGroup<NamedGroupableCampaign> | null;
  labelOf: (key: string) => string;
  nameOf: (wiserId: number) => string;
}

export function indexGroups(catalog: CatalogCampaignLike[]): GroupIndex {
  const rows = [...catalog]
    .sort((a, b) => a.id - b.id)
    .map((c) => ({
      wiserId: c.id,
      name: c.name,
      trafficProvider: c.trafficProvider,
      trafficCampaignId: c.trafficCampaignId ?? c.voluumCampaignId,
    }));
  const groups = groupCampaigns(rows);
  const byKey = new Map(groups.map((g) => [g.key, g]));
  const keyByWiser = new Map<number, string>();
  const names = new Map(rows.map((r) => [r.wiserId, r.name]));
  for (const g of groups) for (const c of g.campaigns) keyByWiser.set(c.wiserId, g.key);
  return {
    groups,
    keyOf: (wiserId) => keyByWiser.get(wiserId) ?? `wiser-${wiserId}`,
    group: (key) => byKey.get(key) ?? null,
    labelOf: (key) => byKey.get(key)?.label ?? key,
    nameOf: (wiserId) => names.get(wiserId) ?? `campaign ${wiserId}`,
  };
}
